import type { LanguageCode } from './languages';

const en = {
  flowBadge: 'Fluid Flow Capacity Tool',
  flowTitle: 'Pipe Flow Rate & Discharge Calculator',
  flowDescription: 'Calculate volumetric discharge from pipe diameter and velocity, or estimate flow capacity from allowable pressure drop.',
  modeVelocity: 'Mode A: From Target Velocity (Q = v × A)',
  modePressure: 'Mode B: From Allowable Pressure Drop (Reverse Darcy)',
  pipeID: 'Pipe Internal Diameter (ID in mm)',
  copperExample: 'Example: 28 mm Copper ID = 26.2 mm',
  fluidVelocity: 'Fluid Velocity (m/s)',
  allowablePressure: 'Allowable ΔP (kPa)',
  length: 'Length (m)',
  litresSecond: 'Litres per second',
  cubicMetresHour: 'Cubic Metres / Hour',
  usGallons: 'US Gallons / Min',
  ukGallons: 'UK Gallons / Min',

  pressureBadge: 'Darcy-Weisbach Formulation',
  pressureTitle: 'Pipe Friction & Pressure Loss Calculator',
  pressureDescription: 'Hydraulic resistance calculation using surface roughness, fluid viscosity, pipe length and fitting losses.',
  pipeMaterial: 'Pipe Material',
  runLength: 'Run Length (m)',
  flowRate: 'Flow Rate (L/s)',
  totalPressureDrop: 'Total Pressure Drop',
  totalHeadLoss: 'Total Head Loss',
  hydraulicGradient: 'Hydraulic Gradient',
  frictionFactor: 'Darcy Friction Factor (f)',
  frictionHead: 'Friction Head Loss',
  minorHead: 'Fitting Head Loss',
  actualVelocity: 'Actual Velocity',
  reynoldsNumber: 'Reynolds Number',
  calculationDetails: 'Calculation Details',
  waterTemperature: 'Water Temperature: 20°C',

  velocityBadge: 'Standards Assessment Matrix',
  velocityTitle: 'Pipe Velocity & Standards Checker',
  velocityDescription: 'Checks actual flow velocity against reference criteria for acoustic comfort, velocity head and pipe longevity.',
  calculatedVelocity: 'Calculated Mean Velocity',
  velocityHead: 'Velocity Head (v²/2g)',
  dynamicPressure: 'Dynamic Pressure',
  compliant: 'Compliant with BS EN 806 & CIBSE',
  nonCompliant: 'Non-compliant: High velocity',
  lowVelocity: 'Low velocity: Stagnation risk',

  invalidID: 'Internal pipe diameter must be greater than 0.',
  invalidVelocity: 'Fluid velocity must be greater than 0.',
  invalidPressure: 'Allowable pressure drop must be greater than 0.',
  invalidLength: 'Pipe length must be greater than 0.',
  invalidFlow: 'Flow rate must be greater than 0.',
  invalidMaterial: 'Unsupported pipe material.',
};

const hi: typeof en = {
  flowBadge: 'जल प्रवाह क्षमता टूल',
  flowTitle: 'पाइप फ्लो रेट और डिस्चार्ज कैलकुलेटर',
  flowDescription: 'पाइप के अंदरूनी व्यास और वेग से पानी के प्रवाह की गणना करें, या अनुमत प्रेशर ड्रॉप के आधार पर प्रवाह क्षमता जानें।',
  modeVelocity: 'मोड A: निर्धारित वेग से (Q = v × A)',
  modePressure: 'मोड B: अनुमत प्रेशर ड्रॉप से (Reverse Darcy)',
  pipeID: 'पाइप का अंदरूनी व्यास (ID, mm)',
  copperExample: 'उदाहरण: 28 mm कॉपर पाइप का ID = 26.2 mm',
  fluidVelocity: 'पानी का वेग (m/s)',
  allowablePressure: 'अनुमत प्रेशर ड्रॉप (kPa)',
  length: 'पाइप की लंबाई (m)',
  litresSecond: 'लीटर प्रति सेकंड',
  cubicMetresHour: 'घन मीटर प्रति घंटा',
  usGallons: 'US गैलन प्रति मिनट',
  ukGallons: 'UK गैलन प्रति मिनट',

  pressureBadge: 'Darcy-Weisbach गणना',
  pressureTitle: 'पाइप घर्षण और प्रेशर लॉस कैलकुलेटर',
  pressureDescription: 'पाइप की सतह की खुरदरापन, पानी की श्यानता, पाइप की लंबाई और फिटिंग लॉस के आधार पर हाइड्रोलिक प्रतिरोध की गणना।',
  pipeMaterial: 'पाइप का मटेरियल',
  runLength: 'पाइप की लंबाई (m)',
  flowRate: 'पानी का प्रवाह (L/s)',
  totalPressureDrop: 'कुल प्रेशर ड्रॉप',
  totalHeadLoss: 'कुल हेड लॉस',
  hydraulicGradient: 'हाइड्रोलिक ग्रेडिएंट',
  frictionFactor: 'Darcy फ्रिक्शन फैक्टर (f)',
  frictionHead: 'घर्षण से हेड लॉस',
  minorHead: 'फिटिंग से हेड लॉस',
  actualVelocity: 'वास्तविक पानी का वेग',
  reynoldsNumber: 'Reynolds नंबर',
  calculationDetails: 'गणना का विवरण',
  waterTemperature: 'पानी का तापमान: 20°C',

  velocityBadge: 'स्टैंडर्ड जांच',
  velocityTitle: 'पाइप वेग और स्टैंडर्ड चेकर',
  velocityDescription: 'पाइप में पानी के वास्तविक वेग की जांच करता है, ताकि शोर, वेग और पाइप की उम्र से जुड़े मानकों का आकलन हो सके।',
  calculatedVelocity: 'गणना किया गया औसत वेग',
  velocityHead: 'वेग हेड (v²/2g)',
  dynamicPressure: 'डायनामिक प्रेशर',
  compliant: 'BS EN 806 और CIBSE के अनुसार',
  nonCompliant: 'मानक के अनुरूप नहीं: वेग अधिक है',
  lowVelocity: 'वेग कम है: पानी रुकने का जोखिम',

  invalidID: 'पाइप का अंदरूनी व्यास 0 से अधिक होना चाहिए।',
  invalidVelocity: 'पानी का वेग 0 से अधिक होना चाहिए।',
  invalidPressure: 'अनुमत प्रेशर ड्रॉप 0 से अधिक होना चाहिए।',
  invalidLength: 'पाइप की लंबाई 0 से अधिक होनी चाहिए।',
  invalidFlow: 'पानी का प्रवाह 0 से अधिक होना चाहिए।',
  invalidMaterial: 'यह पाइप मटेरियल समर्थित नहीं है।',
};

export const calculatorToolTranslations: Partial<
  Record<LanguageCode, typeof en>
> = {
  en,
  hi,
};

export function getCalculatorToolText(language: LanguageCode) {
  return calculatorToolTranslations[language] ?? en;
}